import { useEffect, useState } from 'react';
import { apiFetchSettings } from '../../settings/api';
import type { SettingKey } from '../../../models/Settings';

interface SignaturePrefixValue {
  signaturePrefix?: string;
}

/**
 * Loads SignaturePrefix setting for the selected office.
 * @param officeId - The office ID to load office-scoped setting
 */
export const useSignaturePrefixSetting = (officeId?: string) => {
  const [signaturePrefix, setSignaturePrefix] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!officeId) {
      setSignaturePrefix('');
      setIsLoading(false);
      setError(null);
      return;
    }

    const loadSignaturePrefix = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const settingKey: SettingKey = {
          scopeId: officeId,
          eSettingScope: 'office',
          keys: ['PersonalMailbox_SignaturePrefix'],
        };

        const { data: settings, result } = await apiFetchSettings([settingKey]);

        if (
          result.status >= 200 &&
          result.status < 300 &&
          settings &&
          Array.isArray(settings) &&
          settings.length > 0
        ) {
          const setting = settings[0];
          if (setting.jsonValue) {
            const parsed = JSON.parse(
              setting.jsonValue
            ) as SignaturePrefixValue;
            setSignaturePrefix(parsed.signaturePrefix || '');
          } else {
            setSignaturePrefix('');
          }
        } else {
          setSignaturePrefix('');
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        setSignaturePrefix('');
      } finally {
        setIsLoading(false);
      }
    };

    loadSignaturePrefix();
  }, [officeId]);

  return { signaturePrefix, isLoading, error };
};
